import React from 'react';
import { Box, Container, Typography, Button, Grid, Paper, Card, CardContent, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PersonIcon from '@mui/icons-material/Person';
import ShareIcon from '@mui/icons-material/Share';
import DiamondIcon from '@mui/icons-material/Diamond';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';

const HeroSection = styled(Box)({
  backgroundColor: '#1a1a1a',
  color: 'white',
  padding: '80px 0',
  textAlign: 'center',
});

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '3.5rem',
  marginBottom: '1.5rem',
  '@media (max-width: 600px)': {
    fontSize: '2.5rem',
  }
});

const Subtitle = styled(Typography)({
  fontSize: '1.25rem',
  maxWidth: '800px',
  margin: '0 auto 2rem',
  color: '#c4a661',
});

const StyledButton = styled(Button)({
  backgroundColor: '#c4a661',
  color: 'white',
  padding: '12px 30px',
  fontSize: '1.1rem',
  '&:hover': {
    backgroundColor: '#a38a4a',
  },
});

const FeatureCard = styled(Card)({
  height: '100%',
  backgroundColor: '#fafafa',
  boxShadow: 'none',
  border: '1px solid #eee',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
});

const ExampleProfile = styled(Paper)({
  padding: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
  marginTop: '4rem',
});

const ProfileAvatar = styled(Avatar)({
  width: 120,
  height: 120,
  marginBottom: '1rem',
});

const SocialIcon = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  color: '#666',
  marginBottom: '0.5rem',
});

const FavoriteItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '1rem',
  padding: '1rem',
  backgroundColor: '#fff',
  borderRadius: '4px',
  '& img': {
    width: 80,
    height: 80,
    objectFit: 'cover',
    borderRadius: '4px',
  },
});

const AffiliatePage = () => {
  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageTitle>
              Join Our Luxury Jewelry Affiliate Program
            </PageTitle>
            <Subtitle>
              Partner with us to share exquisite jewelry while earning competitive commissions
            </Subtitle>
            <Box>
              <StyledButton
                disabled
                variant="contained"
                size="large"
                sx={{ mb: 2 }}
              >
                Coming Soon
              </StyledButton>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: '#c4a661',
                  mt: 2 
                }}
              >
                Email us at customconnectjewelry@gmail.com to learn more about our upcoming affiliate program
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontFamily: 'Cormorant Garamond, serif',
            marginBottom: '3rem',
            color: '#1a1a1a',
          }}
        >
          Program Benefits
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <CardContent>
                <MonetizationOnIcon sx={{ fontSize: 40, color: '#c4a661', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Competitive Commission
                </Typography>
                <Typography color="textSecondary">
                  Earn up to 15% commission on every successful referral sale
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <CardContent>
                <PersonIcon sx={{ fontSize: 40, color: '#c4a661', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Personal Profile
                </Typography>
                <Typography color="textSecondary">
                  Create your unique bio and curate your favorite jewelry collection
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>


          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <CardContent>
                <ShareIcon sx={{ fontSize: 40, color: '#c4a661', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Easy Sharing
                </Typography>
                <Typography color="textSecondary">
                  Share your unique links across social media platforms
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FeatureCard>
              <CardContent>
                <DiamondIcon sx={{ fontSize: 40, color: '#c4a661', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Exclusive Access
                </Typography>
                <Typography color="textSecondary">
                  Get early access to new collections and special promotions
                </Typography>
              </CardContent>
            </FeatureCard>
          </Grid>
        </Grid>

        <ExampleProfile>
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Cormorant Garamond, serif',
              marginBottom: '2rem',
              textAlign: 'center',
            }}
          >
            Example Affiliate Profile
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
              <ProfileAvatar src="/path/to/profile.jpg" />
              <Typography variant="h5" gutterBottom>Brooke Anderson</Typography>
              <Box sx={{ mb: 2 }}>
                <SocialIcon>
                  <InstagramIcon /> @brooke.loves.jewelry
                </SocialIcon>
                <SocialIcon>
                  <YouTubeIcon /> Brooke's Jewelry Journey
                </SocialIcon>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>About Me</Typography>
              <Typography paragraph color="textSecondary">
                Like every woman, I believe diamonds are more than just stones – they're stories waiting to be told. As a jewelry enthusiast and content creator, I've spent years exploring the world of fine jewelry and discovering pieces that make hearts skip a beat. From classic solitaires to avant-garde designs, I love sharing my passion for jewelry that makes women feel confident and beautiful.
              </Typography>
              <Typography paragraph color="textSecondary">
                Through my journey in the luxury jewelry space, I've learned that every piece has its own character and charm. I'm excited to curate collections that resonate with my style while helping others find their perfect pieces. Join me in exploring these timeless treasures that add sparkle to our everyday moments.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>My Favorite Pieces</Typography>
              <FavoriteItem>
                <img src="/images/products/cartier-ring.png" alt="Diamond Ring" />
                <Box>
                  <Typography variant="subtitle1">Classic Diamond Solitaire</Typography>
                  <Typography color="textSecondary">
                    "The epitome of timeless elegance. This piece perfectly captures the essence of eternal love."
                  </Typography>
                </Box>
              </FavoriteItem>
            </Grid>
          </Grid>
        </ExampleProfile>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Box>
            <StyledButton
              disabled
              variant="contained"
              size="large"
              sx={{ mb: 2 }}
            >
              Coming Soon
            </StyledButton>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                color: '#c4a661',
                mt: 2 
              }}
            >
              Email us at customconnectjewelry@gmail.com to learn more about our upcoming affiliate program
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AffiliatePage;
